import React from "react";
import { formatRateSheetDate } from "../../ineligibleReasons";
import {
  closingCostSummary,
  creditCost,
  formatMoney,
  formatMoney2,
  formatPrice,
  formatRate,
  termLabel,
} from "./resultModel";
import PriceBreakdown from "./PriceBreakdown";
import AeContacts from "./AeContacts";
import "./RateDetail.css";

// Loan costs and other costs, each folding open to its sections. `open`
// ({ loan, other }) is kept by the caller, so every rate of a product shows
// the same parts open.
function ClosingCost({ row, summary, open, onToggle, onViewFeeSheet }) {
  const part = (key, label, letters, total) => (
    <>
      <tr className="ppe-kv-toggle">
        <td>
          <button type="button" className="ppe-detail-fold" aria-expanded={Boolean(open[key])} onClick={() => onToggle(key)}>
            <span className="ppe-detail-fold-caret" aria-hidden="true">›</span>{label}
          </button>
        </td>
        <td className="r">{formatMoney2(total)}</td>
      </tr>
      {open[key] && summary.sections.filter((s) => letters.includes(s.letter)).map((s) => (
        <tr className="ppe-kv-sub ppe-kv-sub-fold" key={s.letter}>
          <td>{s.letter}. {s.label}</td>
          <td className="r">{formatMoney2(s.total)}</td>
        </tr>
      ))}
    </>
  );
  const isCredit = summary.points < 0;
  return (
    <section className="ppe-detail-section" aria-label="Closing cost">
      <div className="ppe-detail-section-title">
        <h3>Closing cost</h3>
        <span className="ppe-detail-section-sub">at {formatRate(row.rate)}</span>
        <span className="ppe-spacer" />
        <button type="button" className="ppe-link" onClick={() => onViewFeeSheet(row)}>View fee sheet</button>
      </div>
      <table className="ppe-kv">
        <colgroup>
          <col />
          <col className="ppe-kv-col-total" />
        </colgroup>
        <tbody>
          {part("loan", "Loan costs (A+B+C)", "ABC", summary.loanCosts)}
          {part("other", "Other costs (E+F+G+H)", "EFGH", summary.otherCosts)}
          <tr>
            <td>{isCredit ? "Lender credit" : "Discount points"} at {formatRate(row.rate)}</td>
            <td className={`r ${isCredit ? "ppe-good" : ""}`}>{isCredit ? "−" : ""}{formatMoney2(Math.abs(summary.points))}</td>
          </tr>
          <tr className="ppe-kv-total">
            <td>Cost to borrower</td>
            <td className="r">{formatMoney2(summary.totalClosingCost)}</td>
          </tr>
          <tr className="ppe-kv-big">
            <td>Cash to close{summary.downPayment ? <span className="ppe-muted"> (down payment {formatMoney(summary.downPayment)} + cost to borrower)</span> : null}</td>
            <td className="r">{formatMoney2(summary.cashToClose)}</td>
          </tr>
        </tbody>
      </table>
    </section>
  );
}

// Everything about one rate (D27; in the results table beside the product's
// rates since 11-04, where the right-side drawer was): rate, APR, final
// price, credit or cost and PMT; the price breakdown with the rate sheet; the
// closing cost with its fee sheet; the rate sheet's date. Under the rate
// line, the lender's Account Executive (`aeContacts`, the same for every
// rate of the product; nothing when there is none).
export default function RateDetail({
  product,
  aeContacts,
  row,
  loanAmount,
  lockDays,
  compensationType,
  appliedClosingCostsDetail,
  downPayment,
  purpose,
  open,
  onToggle,
  onViewFeeSheet,
}) {
  const cc = creditCost(row.finalAmount);
  const termsUrl = product.fileInfo && product.fileInfo.TermsUrl;
  const summary = closingCostSummary(row, appliedClosingCostsDetail, { downPayment, purpose });
  return (
    <div className="ppe-detail">
      <div className="ppe-detail-head">
        <div>
          <span className="ppe-detail-rate">{formatRate(row.rate)}</span>{" "}
          <span className="ppe-muted">{formatRate(row.apr)} APR · {termLabel(product.term)}</span>
        </div>
        <div className="ppe-detail-sub">
          Final price <b className={row.final >= 100 ? "ppe-good" : ""}>{formatPrice(row.final)}</b> · <span className={cc.kind === "credit" ? "ppe-good" : ""}>{cc.text}</span> · PMT {formatMoney(row.pmt)}
        </div>
      </div>
      <AeContacts contacts={aeContacts} lenderName={product.lenderName} />
      <PriceBreakdown
        product={product}
        row={row}
        loanAmount={loanAmount}
        lockDays={lockDays}
        compensationType={compensationType}
        actions={termsUrl && (
          <button type="button" className="ppe-link" onClick={() => window.open(termsUrl, "_blank")}>Download rate sheet</button>
        )}
      />
      <ClosingCost row={row} summary={summary} open={open} onToggle={onToggle} onViewFeeSheet={onViewFeeSheet} />
      {product.rateSheetDate && (
        <div className="ppe-muted ppe-detail-foot">Rate sheet {formatRateSheetDate(product.rateSheetDate)}{product.staleRateSheet ? " (older than the previous business day)" : ""}</div>
      )}
    </div>
  );
}
